import { Consultation, Patient, Product } from '@app/core/models';
import { ProductCatalogFilterValues } from '@app/shared/components/product-catalog-filters/product-catalog-filters';
import { LifestageType, ProductPillar, ProductType, SpeciesCode, Tool } from '@app/shared/utils/enums';
import { RangeType } from '@app/shared/utils/enums/rangeType.enum';
import { SearchChoice } from '@app/shared/utils/enums/search-choice.enum';
import { TerritoryType } from '@app/shared/utils/enums/territoryType';
import {
  DailyAllocationButtonsActionGtm,
  EventEnum,
  LabelEnum,
  ModuleNameEnum,
  PageBlockEnum,
  PageTypeEnum,
  ProductBookButtonActionGtm,
  shareRecommendationButtonActionGtm,
  TopNavLinkGtmEnum,
} from '../types/enums';
import { GTMInteraction } from '../types/interfaces';
import { gtmPetInfoToPetInteraction, gtmVisitType } from '@app/shared/utils/static-helpers/gtm-helper';

export const continueToProducts = (name: ModuleNameEnum, pathology?: string): GTMInteraction => ({
  event: EventEnum.CONTINUE_TO_PRODUCTS,
  module: { name },
  pet: { pathology: pathology || 'undefined' },
});

export const continueToRation = (name: ModuleNameEnum): GTMInteraction => ({
  event: EventEnum.CONTINUE_TO_RATION,
  module: { name },
});

export const printOrderSummary = (name: ModuleNameEnum, type: PageTypeEnum, block: PageBlockEnum, language?: string): GTMInteraction => ({
  event: EventEnum.PRINT,
  module: { name },
  page: { block, type },
  language,
});

export const shareRecoTracking = (
  action: shareRecommendationButtonActionGtm,
  name: ModuleNameEnum,
  purchaseLink: string,
  language?: string
): GTMInteraction => ({
  event: EventEnum.SHARE_RECOMMENDATION_BUTTON,
  shareRecommendationButton: { action },
  module: { name },
  ...(purchaseLink && { purchaseLink }),
  language,
});

export const productEditPrices = (): GTMInteraction => ({
  event: EventEnum.PRODUCT_EDIT_PRICES,
});

export const productSavePrices = (): GTMInteraction => ({
  event: EventEnum.PRODUCT_SAVE_PRICES,
});

export const homeCardInteraction = (label: LabelEnum): GTMInteraction => ({
  event: EventEnum.MODULE_CLICK,
  interaction: {
    label,
  },
});

export const quickLinksInteraction = (label: LabelEnum): GTMInteraction => ({
  event: EventEnum.QUICK_LINK_CLICK,
  interaction: {
    label,
  },
});

export const homeSearchInteraction = (): GTMInteraction => ({
  event: EventEnum.HOME_SEARCH_CLICK,
});

export const startDetection: GTMInteraction = {
  event: EventEnum.START_DETECTION,
  module: { name: ModuleNameEnum.RENAL_DETECT },
};

export const criteriaModalDisplayed = ({ name, relatedProduct }): GTMInteraction => ({
  event: EventEnum.CRITERIA_MODAL_DISPLAYED,
  module: {
    name: ModuleNameEnum.SMART_RECOMMENDATION,
  },
  criteria: {
    name,
    relatedProduct,
  },
});

export const productDetailsDisplayed = ({ name, id }: Product): GTMInteraction => ({
  event: EventEnum.PRODUCT_DETAILS_DISPLAYED,
  product: {
    name,
    id,
  },
});

export const petRecordSearchClick = (filter_by: SearchChoice, filter_species: SpeciesCode): GTMInteraction => ({
  event: EventEnum.PET_RECORD_SEARCH_CLICK,
  search: {
    activeFilters: [
      {
        By: filter_by,
        Species: filter_species === SpeciesCode.Unknown ? 'All' : filter_species,
      },
    ],
  },
});

export const smartRecoSearchAddProduct: GTMInteraction = {
  event: EventEnum.PRODUCT_ADD_CLICK,
};

export const addProductClick: GTMInteraction = {
  event: EventEnum.PRODUCT_ADD_CLICK,
};

export const productSearchClick = (currentFilters?: ProductCatalogFilterValues): GTMInteraction => {
  const technologyFilter = [];
  if (currentFilters.dry) technologyFilter.push(ProductType.Dry);
  if (currentFilters.wet) technologyFilter.push(ProductType.Wet);
  if (!currentFilters.wet && !currentFilters.dry) technologyFilter.push(ProductType.Unknown);

  const currLifestage = [];
  if (currentFilters.adult) {
    currLifestage.push(LifestageType.Adult);
  }
  if (currentFilters.birthAndGrowth) {
    currLifestage.push('Birth & Growth');
  }
  if (currentFilters.mature) {
    currLifestage.push(LifestageType.Mature);
  }
  return {
    event: EventEnum.PRODUCT_SEARCH_CLICK,
    search: {
      query: currentFilters?.search?.length > 2 ? currentFilters?.search : '',
      activeFilters: [
        {
          Technology: technologyFilter,
          Lifestage: currLifestage,
          Size: currentFilters.size,
          Species: currentFilters.specie === SpeciesCode.Unknown ? 'All' : currentFilters.specie,
          Range: currentFilters.pillar === ProductPillar.VET ? 'Veterinary' : 'Health nutrition',
        },
      ],
    },
  };
};

export const weightManagementFromTool = (): GTMInteraction => ({
  event: EventEnum.WEIGHT_MANAGEMENT_FROM_TOOL,
});

export const territoryNavigationClick = (selectedTerritoy: TerritoryType | RangeType): GTMInteraction => ({
  event: EventEnum.TERRITORY_NAVIGATION_CLICK,
  territory: selectedTerritoy,
});

export const smartRecoRetry = (): GTMInteraction => ({
  event: EventEnum.RETRY_CLICK,
});

export const smartRecoFeedback = (nbProductResults: number): GTMInteraction => ({
  event: EventEnum.SEND_FEEDBACK_CLICK,
  nbProductResults,
});

export const petPanelClick = (editActionType: LabelEnum): GTMInteraction => ({
  event: EventEnum.PET_PANEL_CLICK,
  interaction: {
    label: editActionType,
  },
});

export const warningAutomaticRationing: GTMInteraction = {
  event: EventEnum.WARNING_AUTOMATIC_RATIONING,
};

export const automaticRationingCalculation = (value): GTMInteraction => ({
  event: EventEnum.AUTOMATIC_RATIONING_CALCULATION,
  automaticRationingCalculation: value ? 'enable' : 'disable',
});

export const wetAllowanceSelection = (value): GTMInteraction => ({
  event: EventEnum.WET_ALLOWANCE_SELECTION,
  wetAllowanceSelection: {
    allowance: value,
  },
});

export const resetDailyRation = {
  event: EventEnum.RESET_DAILY_RATION,
};

export const patientBlockActive = ({ block, type }): GTMInteraction => ({
  event: EventEnum.PATIENT_BLOCK_ACTIVE,
  page: {
    block,
    type,
  },
});

export const dailyAllocationButton = (action: DailyAllocationButtonsActionGtm, purchaseRedirect?: string): GTMInteraction => ({
  event: EventEnum.DAILYALLOCATIONBUTTON,
  dailyAllocationButton: purchaseRedirect ? { action, purchaseRedirect } : { action },
});

export const topNavClickGtm = (name: TopNavLinkGtmEnum): GTMInteraction => ({
  event: EventEnum.TOP_NAV_CLICK,
  topNavClick: { name },
});

export const productBookButton = (action: ProductBookButtonActionGtm, purchaseRedirect: string): GTMInteraction => ({
  event: EventEnum.PRODUCT_BOOK_BUTTON,
  productBookButton: { action, purchaseRedirect },
});

export const startConsultationExistingPatientGtm = ({
  tool,
  patient,
  lastConsultation,
}: {
  tool: Tool;
  patient: Patient;
  lastConsultation: Consultation | null;
}): GTMInteraction => {
  return {
    event: EventEnum.CONSULTATION_STARTED,
    consultation: { type: gtmVisitType(tool) },
    pet: gtmPetInfoToPetInteraction(patient, lastConsultation),
  };
};

export const startConsultationGtm = (tool: Tool): GTMInteraction => {
  return {
    event: EventEnum.CONSULTATION_STARTED,
    consultation: { type: gtmVisitType(tool) },
    pet: undefined,
  };
};

export const sendPetIdentifiedGtm = ({
  patient,
  lastConsultation,
}: {
  patient: Patient | null;
  lastConsultation: Consultation | null;
}): GTMInteraction => {
  return {
    event: EventEnum.PET_IDENTIFIED,
    pet: gtmPetInfoToPetInteraction(patient, lastConsultation),
  };
};
